import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

import {
  AlertTriangle,
  BookOpen,
  CalendarDays,
  Cpu,
  ChevronDown,
  ChevronRight,
  CreditCard,
  FileBarChart,
  LayoutDashboard,
  LogOut,
  MessageSquare,
  KeyRound,
  Menu,
  School,
  ShieldCheck,
  TrendingUp,
  UploadCloud,
  UserCog,
  Users,
  X,
  Moon,
  Sun,
} from "lucide-react";

import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import api from "@/lib/api";
import { cn } from "@/lib/utils";
import { KORWIL_LIST } from "@/components/Shared";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";


/* ============================================================
   NAVIGATION
   ============================================================ */

const NAV = [
  {
    title: "Command Center",
    path: "/",
    icon: LayoutDashboard,
    id: "dashboard",
  },
  {
    title: "Data Alokasi",
    id: "data-alokasi",
    children: [
      {
        title: "Upload Alokasi",
        path: "/jadwal/upload",
        icon: UploadCloud,
        id: "upload-alokasi",
      },
      {
        title: "Jadwal Alokasi Pusat",
        path: "/jadwal",
        icon: CalendarDays,
        id: "jadwal",
      },
      {
        title: "Konfirmasi Jadwal",
        path: "/unit",
        icon: Cpu,
        id: "unit",
      },
      {
        title: "Database Sekolah",
        path: "/sekolah",
        icon: School,
        id: "sekolah",
      },
    ],
  },
  {
    title: "Master Data Teknisi",
    path: "/teknisi",
    icon: Users,
    id: "teknisi",
  },
  {
    title: "Perlu Tindakan",
    path: "/attention",
    icon: AlertTriangle,
    id: "attention",
  },
  {
    title: "Broadcast",
    path: "/broadcast",
    icon: MessageSquare,
    id: "broadcast",
  },
  {
    title: "Korwil Monitoring",
    path: "/korwil",
    icon: ShieldCheck,
    id: "korwil",
  },
  {
    title: "LMS",
    path: "/lms",
    icon: BookOpen,
    id: "lms",
  },
  {
    title: "Report",
    path: "/report",
    icon: FileBarChart,
    id: "report",
  },
  {
    title: "User Management",
    path: "/users",
    icon: UserCog,
    id: "user-management",
  },
  {
    title: "Finance",
    id: "finance",
    children: [
      { title: "Dashboard", path: "/finance?menu=dashboard", icon: LayoutDashboard, id: "finance-dashboard" },
      { title: "Pembayaran BAPP KORDA", path: "/finance?menu=pembayaran_bapp_korda", icon: CreditCard, id: "finance-pembayaran-bapp-korda" },
      { title: "Setoran BAPP Pusat", path: "/finance?menu=setoran_bapp_pusat", icon: FileBarChart, id: "finance-setoran" },
      { title: "Pembayaran dari Pusat", path: "/finance?menu=pusat", icon: CreditCard, id: "finance-pusat" },
      { title: "Pembayaran ke Korda", path: "/finance?menu=korda", icon: CreditCard, id: "finance-korda" },
      { title: "Riwayat Pembayaran", path: "/finance?menu=riwayat", icon: FileBarChart, id: "finance-riwayat" },
      { title: "Master Fee & Pembagian Margin", path: "/finance?menu=master_fee", icon: CreditCard, id: "finance-master-fee" },
      { title: "Biaya Operasional", path: "/finance?menu=operasional", icon: CreditCard, id: "finance-operasional" },
      { title: "Laporan Finance", path: "/finance?menu=laporan", icon: FileBarChart, id: "finance-laporan" },
    ],
  },
];


/* ============================================================
   ROLE
   ============================================================ */

const FINANCE_ROLES = [
  "SUPER_ADMIN",
  "KORWIL",
  "KORDA",
  "KOPROV",
  "ADMIN_KORWIL",
];


/* ============================================================
   LAYOUT
   ============================================================ */

export default function Layout({ children }) {
  const {
    user,
    logout,
    korwilFilter,
    setKorwilFilter,
    kordaFilter,
    setKordaFilter,
  } = useAuth();

  const [open, setOpen] = useState(false);
  const [dataAlokasiOpen, setDataAlokasiOpen] = useState(false);
  const [financeOpen, setFinanceOpen] = useState(false);
  const [alerts, setAlerts] = useState(0);
  const [broadcastUnread, setBroadcastUnread] = useState(0);
  const [kordaOptions, setKordaOptions] = useState([]);

  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const location = useLocation();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();

  const isNavChildActive = (path) => {
    const parts = path.split("?");
    const targetPath = parts[0];
    const targetSearch = parts[1] ? "?" + parts[1] : "";
    if (location.pathname !== targetPath) return false;
    return targetSearch ? location.search === targetSearch : true;
  };


  /* ============================================================
     CLOSE MOBILE SIDEBAR
     ============================================================ */

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const dataAlokasiPaths = [
      "/jadwal/upload",
      "/jadwal",
      "/unit",
      "/sekolah",
    ];

    if (dataAlokasiPaths.includes(location.pathname)) {
      setDataAlokasiOpen(true);
    }
    if (location.pathname === "/finance") {
      setFinanceOpen(true);
    }
  }, [location.pathname]);


  /* ============================================================
     LOAD KORDA OPTIONS
     ============================================================ */

  useEffect(() => {
    if (user?.role === "KORDA") {
      return;
    }

    api
      .get("/filters/options")
      .then(({ data }) => {
        setKordaOptions(data.korda || []);
      })
      .catch(() => {});
  }, [user]);


  /* ============================================================
     LOAD ALERT
     ============================================================ */

  useEffect(() => {
    api
      .get("/attention/summary", {
        params: {
          korwil: korwilFilter || undefined,
        },
      })
      .then(({ data }) => {
        // Badge harus menghitung transaksi unik, bukan menjumlahkan kategori.
        // Satu transaksi dapat masuk NO_ACTUAL_TECH + NOT_CONFIRMED + OVERDUE sekaligus.
        setAlerts(Number(data.total_urgent ?? 0));
      })
      .catch(() => {});
  }, [korwilFilter, location.pathname]);

  useEffect(() => {
    api
      .get("/broadcast/unread-count")
      .then(({ data }) => setBroadcastUnread(Number(data.count ?? 0)))
      .catch(() => {});
  }, [location.pathname]);


  /* ============================================================
     CHANGE PASSWORD
     ============================================================ */

  const changePassword = async (e) => {
    e.preventDefault();

    setPasswordError("");
    setPasswordMessage("");

    if (newPassword.length < 8) {
      setPasswordError(
        "Password baru minimal 8 karakter."
      );
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError(
        "Konfirmasi password tidak sama."
      );
      return;
    }

    setPasswordBusy(true);

    try {
      const { data } = await api.post(
        "/auth/change-password",
        {
          current_password: currentPassword,
          new_password: newPassword,
          confirm_password: confirmPassword,
        }
      );

      setPasswordMessage(
        data?.message ||
        "Password berhasil diubah."
      );

      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      const detail = err?.response?.data?.detail;

      setPasswordError(
        typeof detail === "string"
          ? detail
          : "Gagal mengubah password."
      );
    } finally {
      setPasswordBusy(false);
    }
  };


  /* ============================================================
     CLOSE PASSWORD DIALOG
     ============================================================ */

  const closeChangePassword = () => {
    if (passwordBusy) {
      return;
    }

    setChangePasswordOpen(false);
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setPasswordMessage("");
    setPasswordError("");
  };


  /* ============================================================
     ROLE FLAGS
     ============================================================ */

  const isKorwilUser =
    user?.role === "KORWIL";

  // Semua role berikut boleh membuka /finance.
  // Hak menu diatur lebih lanjut oleh Finance.jsx.
  const canAccessFinance =
    FINANCE_ROLES.includes(user?.role);


  /* ============================================================
     NAVIGATION FILTER
     
     Finance:
       SUPER_ADMIN  -> tampil
       KORWIL       -> tampil
       KORDA        -> tampil (Finance KORDA)
       ADMIN_KORWIL -> tampil (Setoran BAPP Pusat saja)

     Upload:
       SUPER_ADMIN  -> tampil
       KORWIL       -> tampil
       ADMIN_KORWIL -> tampil
       KORDA        -> hidden
     ============================================================ */

  const canShowNavItem = (item) => {
  if (item.id === "user-management" && user?.role !== "SUPER_ADMIN") return false;
  if (item.id === "upload-alokasi" && ["KORDA", "KOPROV"].includes(user?.role)) return false;
  if (item.id === "finance" && !canAccessFinance) return false;
  if (item.id?.startsWith("finance-") && user?.role === "ADMIN_KORWIL") {
    return item.id === "finance-setoran";
  }
  if (item.id === "finance-pembayaran-bapp-korda") {
    return user?.role === "KORDA";
  }
  if (item.id?.startsWith("finance-") && ["KORDA", "KOPROV"].includes(user?.role)) {
    return [
      "finance-dashboard",
    ].includes(item.id);
  }
  return true;
};

const visibleNav = NAV.map((item) => {
  if (!item.children) return canShowNavItem(item) ? item : null;
  const children = item.children.filter(canShowNavItem);
  return children.length > 0 ? { ...item, children } : null;
}).filter(Boolean);


  return (
    <div className="min-h-screen bg-[#0b0f17]">


      {/* ======================================================
          SIDEBAR
          ====================================================== */}

      <aside
        data-testid="sidebar"
        className={cn(
          "fixed inset-y-0 left-0 z-40 w-60 border-r border-slate-800 bg-[#0d121c] flex flex-col transition-transform duration-200 lg:translate-x-0",
          open
            ? "translate-x-0"
            : "-translate-x-full"
        )}
      >


        {/* ====================================================
            LOGO
            ==================================================== */}

        <div className="flex items-center gap-2 border-b border-slate-800 px-4 py-4">

          <div className="flex h-8 w-8 items-center justify-center rounded bg-blue-600 font-display font-extrabold text-white">
            P
          </div>

          <div className="leading-tight">
            <div className="font-display text-xs font-extrabold tracking-tight text-white leading-tight">
              PYX KORDA - MONITORING INSTALASI
            </div>
          </div>

          <button
            data-testid="sidebar-close"
            onClick={() => setOpen(false)}
            className="ml-auto text-slate-400 lg:hidden"
          >
            <X className="h-5 w-5" />
          </button>

        </div>


        {/* ====================================================
            NAVIGATION
            ==================================================== */}

        <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-0.5">

          {visibleNav.map((n) => (
  n.children ? (
    <div key={n.id} className="pt-1">
      <button
        type="button"
        data-testid={`nav-group-${n.id}`}
        aria-expanded={n.id === "finance" ? financeOpen : dataAlokasiOpen}
        onClick={() => {
          if (n.id === "finance") {
            setFinanceOpen((value) => !value);
          } else {
            setDataAlokasiOpen((value) => !value);
          }
        }}
        className={cn(
          "group flex w-full items-center gap-2.5 rounded px-3 py-2 text-sm font-semibold transition-colors",
          n.children.some((child) => isNavChildActive(child.path))
            ? "bg-slate-800/60 text-white"
            : "text-slate-300 hover:bg-slate-800 hover:text-white"
        )}
      >
        {(n.id === "finance" ? financeOpen : dataAlokasiOpen) ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-slate-500" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-slate-500" />
        )}
        <span className="truncate">{n.title}</span>
      </button>

      {(n.id === "finance" ? financeOpen : dataAlokasiOpen) && (
        <div className="mt-0.5 space-y-0.5">
          {n.children.map((child) => (
            <NavLink
              key={child.id}
              to={child.path}
              data-testid={`nav-${child.id}`}
              className={() => cn(
                "group ml-2 flex items-center gap-2.5 rounded px-3 py-2 text-sm font-medium transition-colors",
                isNavChildActive(child.path)
                  ? "bg-blue-600/15 !text-white border-l-2 border-blue-500"
                  : "text-slate-400 hover:bg-slate-800 hover:text-white border-l-2 border-transparent"
              )}
            >
              <child.icon className="h-4 w-4 shrink-0" />
              <span className="truncate">{child.title}</span>
            </NavLink>
          ))}
        </div>
      )}
    </div>
  ) : (
    <NavLink
      key={n.id}
      to={n.path}
      data-testid={`nav-${n.id}`}
      className={({ isActive }) => cn(
        "group flex items-center gap-2.5 rounded px-3 py-2 text-sm font-medium transition-colors",
        isActive
          ? "bg-blue-600/15 !text-white border-l-2 border-blue-500"
          : "text-slate-400 hover:bg-slate-800 hover:text-white border-l-2 border-transparent"
      )}
    >
      <n.icon className="h-4 w-4 shrink-0" />
      <span className={cn("truncate", (n.id === "attention" || n.id === "broadcast") && "!text-white")}>{n.title}</span>
      {n.id === "attention" && alerts > 0 && (
        <span className="ml-auto rounded-full bg-red-600 px-1.5 py-0.5 font-mono-data text-[10px] font-bold !text-white">{alerts}</span>
      )}
      {n.id === "broadcast" && broadcastUnread > 0 && (
        <span className="ml-auto rounded-full bg-red-600 px-1.5 py-0.5 font-mono-data text-[10px] font-bold !text-white">{broadcastUnread}</span>
      )}
    </NavLink>
  )
))}

        </nav>


        {/* ====================================================
            USER FOOTER
            ==================================================== */}

        <div className="border-t border-slate-800 p-3">

          <div
            className="mb-2 truncate text-xs text-slate-300"
            data-testid="sidebar-user"
          >
            {user?.name}
          </div>

          <div
            className="mb-2 text-[10px] uppercase tracking-wider text-slate-500"
            data-testid="sidebar-scope"
          >
            {user?.role}

            {user?.korda
              ? ` · ${user.korda}`
              : ""}

            {user?.korwil
              ? ` · ${user.korwil}`
              : ""}
          </div>


          {/* ==================================================
              CHANGE PASSWORD
              ================================================== */}

          <button
            type="button"
            data-testid="btn-change-password"
            onClick={() => {
              setPasswordError("");
              setPasswordMessage("");
              setChangePasswordOpen(true);
            }}
            className="mb-2 flex w-full items-center gap-2 rounded border border-slate-700 px-2.5 py-1.5 text-xs text-slate-300 transition-colors hover:bg-slate-800 hover:text-white"
          >

            <KeyRound className="h-3.5 w-3.5" />

            Ganti Password

          </button>


          {/* ==================================================
              LOGOUT
              ================================================== */}

          <button
            data-testid="btn-logout"
            onClick={async () => {
              await logout();
              navigate("/login");
            }}
            className="flex w-full items-center gap-2 rounded border border-slate-700 px-2.5 py-1.5 text-xs text-slate-300 transition-colors hover:bg-red-950/60 hover:text-red-200"
          >

            <LogOut className="h-3.5 w-3.5" />

            Keluar

          </button>

        </div>

      </aside>


      {/* ======================================================
          MOBILE OVERLAY
          ====================================================== */}

      {open && (
        <div
          className="fixed inset-0 z-30 bg-black/60 lg:hidden"
          onClick={() => setOpen(false)}
        />
      )}


      {/* ======================================================
          MAIN
          ====================================================== */}

      <div className="lg:pl-60">


        {/* ====================================================
            HEADER
            ==================================================== */}

        <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-slate-800 bg-[#0b0f17]/90 px-4 py-3 backdrop-blur-md">

          <button
            data-testid="sidebar-toggle"
            aria-label="menu"
            onClick={() => setOpen(true)}
            className="text-slate-300 lg:hidden"
          >
            <Menu className="h-5 w-5" />
          </button>


          <div className="hidden text-xs text-slate-500 sm:block">
            {user?.role === "KORDA"
              ? `KORDA ${user?.korda || "-"}`
              : "Korda"}
          </div>


          {/* ==================================================
              KORDA SELECTOR
              ================================================== */}

          {user?.role !== "KORDA" && (

            <select
              data-testid="korda-selector"
              value={kordaFilter}
              onChange={(e) =>
                setKordaFilter(e.target.value)
              }
              className="rounded border border-slate-700 bg-slate-900 px-2 py-1 text-xs text-slate-200 outline-none focus:border-blue-500"
            >

              <option value="">
                Semua Korda
              </option>

              {kordaOptions.map((k) => (

                <option
                  key={k}
                  value={k}
                >
                  {k}
                </option>

              ))}

            </select>

          )}


          {/* ==================================================
              KORWIL FILTER
              ================================================== */}

          <div className="flex flex-wrap items-center gap-1.5">

            <button
              data-testid="korwil-filter-all"
              disabled={isKorwilUser}
              onClick={() => setKorwilFilter("")}
              className={cn(
                "rounded px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide transition-colors disabled:opacity-40",

                !korwilFilter
                  ? "bg-blue-600 text-white"
                  : "border border-slate-700 text-slate-400 hover:bg-slate-800 hover:text-white"
              )}
            >
              Semua
            </button>


            {KORWIL_LIST.map((k) => (

              <button
                key={k}
                data-testid={`korwil-filter-${k.toLowerCase()}`}
                disabled={
                  isKorwilUser &&
                  user.korwil !== k
                }
                onClick={() =>
                  setKorwilFilter(k)
                }
                className={cn(
                  "rounded px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide transition-colors disabled:opacity-30",

                  korwilFilter === k
                    ? "bg-blue-600 text-white"
                    : "border border-slate-700 text-slate-400 hover:bg-slate-800 hover:text-white"
                )}
              >
                {k}
              </button>

            ))}

          </div>


          {/* ==================================================
              ALERT
              ================================================== */}

          <button
            type="button"
            data-testid="theme-toggle"
            aria-label={theme === "dark" ? "Aktifkan Light Mode" : "Aktifkan Dark Mode"}
            title={theme === "dark" ? "Light Mode" : "Dark Mode"}
            onClick={toggleTheme}
            className="ml-auto inline-flex shrink-0 items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
          >
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            <span className="hidden sm:inline">{theme === "dark" ? "Light" : "Dark"}</span>
          </button>

          <Link
            to="/attention"
            data-testid="header-alert-link"
            className="ml-2 flex items-center gap-1.5 rounded border border-red-900/70 bg-red-950/40 px-2.5 py-1 text-[11px] font-semibold text-red-300 transition-colors hover:bg-red-900/50"
          >

            <AlertTriangle className="h-3.5 w-3.5" />

            <span className="font-mono-data">
              {alerts}
            </span>

            <span className="hidden sm:inline">
              Perlu Tindakan
            </span>

          </Link>

        </header>


        {/* ====================================================
            CONTENT
            ==================================================== */}

        <main
          className="p-4 sm:p-6"
          data-testid="main-content"
        >
          {children}
        </main>

      </div>


      {/* ======================================================
          CHANGE PASSWORD DIALOG
          ====================================================== */}

      <Dialog
        open={changePasswordOpen}
        onOpenChange={(isOpen) => {

          if (!isOpen) {
            closeChangePassword();
          } else {
            setChangePasswordOpen(true);
          }

        }}
      >

        <DialogContent className="border-slate-700 bg-[#0d121c] text-white">

          <DialogHeader>

            <DialogTitle className="text-white">
              Ganti Password
            </DialogTitle>

            <DialogDescription className="text-slate-400">
              Ubah password akun Anda.
              Password baru minimal 8 karakter.
            </DialogDescription>

          </DialogHeader>


          <form
            onSubmit={changePassword}
            className="space-y-4"
          >

            {/* Password lama */}

            <div>

              <label className="text-[10px] uppercase tracking-wider text-slate-500">
                Password Lama
              </label>

              <input
                type="password"
                required
                autoComplete="current-password"
                value={currentPassword}
                onChange={(e) =>
                  setCurrentPassword(
                    e.target.value
                  )
                }
                className="mt-1 w-full rounded border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white outline-none focus:border-blue-500"
                placeholder="Masukkan password lama"
              />

            </div>


            {/* Password baru */}

            <div>

              <label className="text-[10px] uppercase tracking-wider text-slate-500">
                Password Baru
              </label>

              <input
                type="password"
                required
                minLength={8}
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) =>
                  setNewPassword(
                    e.target.value
                  )
                }
                className="mt-1 w-full rounded border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white outline-none focus:border-blue-500"
                placeholder="Minimal 8 karakter"
              />

            </div>


            {/* Konfirmasi */}

            <div>

              <label className="text-[10px] uppercase tracking-wider text-slate-500">
                Konfirmasi Password Baru
              </label>

              <input
                type="password"
                required
                minLength={8}
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) =>
                  setConfirmPassword(
                    e.target.value
                  )
                }
                className="mt-1 w-full rounded border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white outline-none focus:border-blue-500"
                placeholder="Ulangi password baru"
              />

            </div>


            {/* Error */}

            {passwordError && (

              <div className="rounded border border-red-800 bg-red-950/60 px-3 py-2 text-xs text-red-300">
                {passwordError}
              </div>

            )}


            {/* Success */}

            {passwordMessage && (

              <div className="rounded border border-emerald-800 bg-emerald-950/50 px-3 py-2 text-xs text-emerald-300">
                {passwordMessage}
              </div>

            )}


            {/* Footer */}

            <DialogFooter>

              <button
                type="button"
                disabled={passwordBusy}
                onClick={closeChangePassword}
                className="rounded border border-slate-700 px-3 py-2 text-sm text-slate-300 hover:bg-slate-800 disabled:opacity-50"
              >
                Batal
              </button>


              <button
                type="submit"
                disabled={passwordBusy}
                className="rounded bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {passwordBusy
                  ? "Menyimpan..."
                  : "Simpan Password"}
              </button>

            </DialogFooter>

          </form>

        </DialogContent>

      </Dialog>

    </div>
  );
}
